import { Link, useSearchParams } from "react-router-dom";
import { BiSearch, BiSolidHome } from "react-icons/bi";
import { AiOutlineClose } from "react-icons/ai";
import {
  MdKeyboardDoubleArrowRight,
  MdKeyboardDoubleArrowLeft,
} from "react-icons/md";
import Navbar from "../../components/navbar/Navbar";
import Footer from "../../components/footer/Footer";
import { useEffect, useState } from "react";
import ProductCard from "../../components/ProductCard/ProductCard";

const ProductListView = () => {
  const [searchParams] = useSearchParams();
  const categoryFromURL = searchParams.get("category") || "All";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(categoryFromURL);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;

  const categoryList = [
    { name: "Snacks", icon: "🍿" },
    { name: "Fish", icon: "🐠" },
    { name: "Paratha", icon: "🫓" },
    { name: "Vegetables", icon: "🥦" },
    { name: "Bakery & Sweets", icon: "🍰" },
  ];

  useEffect(() => {
    fetch("https://backend-server-nine-opal.vercel.app/contents")
      .then((res) => res.json())
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
      });
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Filtered products
  const filteredData = data
    .filter((item) =>
      selectedCategory === "All"
        ? true
        : item.product_Category?.toLowerCase().trim() ===
          selectedCategory.toLowerCase().trim(),
    )
    .filter((item) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        item.Product_name?.toLowerCase().includes(term) ||
        item.brand_Name?.toLowerCase().includes(term)
      );
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredData.slice(
    indexOfFirstProduct,
    indexOfLastProduct,
  );

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      scrollToTop();
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      scrollToTop();
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    scrollToTop();
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="container px-4 lg:px-0 mx-auto py-16">
        {/* Breadcrumb */}
        <div className="text-sm breadcrumbs mb-4">
          <ul>
            <li>
              <Link
                to="/"
                onClick={scrollToTop}
                className="flex items-center gap-1 hover:text-[#1DA14D] hover:font-semibold"
              >
                <BiSolidHome className="text-lg" />
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/products"
                onClick={scrollToTop}
                className="flex items-center hover:text-[#1DA14D] hover:font-semibold gap-1"
              >
                Product
              </Link>
            </li>
            <li>Our product list</li>
          </ul>
        </div>

        <div className="border-b-2 border-[#1C3A27] pb-4 mb-6">
          {/* Page Title */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="text-left">
              <h1 className="text-3xl font-bold text-gray-800">
                Choose Your Best Tour Plan
              </h1>
              <p className="text-sm text-gray-500">
                Some of our picked Spots near your location.
              </p>
            </div>
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap justify-between gap-3 items-center mb-4">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => {
                  setSelectedCategory("All");
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-full border whitespace-nowrap ${
                  selectedCategory === "All"
                    ? "bg-black text-white text-xs lg:text-base border-black"
                    : "bg-white text-black text-xs lg:text-base border-gray-300"
                }`}
              >
                All <span className="text-xs lg:text-base ml-2">📦</span>
              </button>
              {categoryList.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => {
                    setSelectedCategory(cat.name);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-full border whitespace-nowrap ${
                    selectedCategory === cat.name
                      ? "bg-black text-white text-xs lg:text-base border-black"
                      : "bg-white text-black text-xs lg:text-base border-gray-300"
                  }`}
                >
                  {cat.name}{" "}
                  <span className="ml-2 text-xs lg:text-base">{cat.icon}</span>
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-64">
              <BiSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                size={20}
              />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1DA14D]"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                >
                  <AiOutlineClose size={18} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, idx) => (
              <div
                key={idx}
                className="animate-pulse border rounded-lg shadow-md p-4"
              >
                <div className="bg-gray-300 h-40 w-full rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-600 text-right mb-4">
              Showing {filteredData.length === 0 ? 0 : indexOfFirstProduct + 1}–
              {Math.min(indexOfLastProduct, filteredData.length)} of{" "}
              {filteredData.length} results
            </div>

            {currentProducts.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No products found in this category.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {currentProducts.map((product) => (
                    <ProductCard key={product._id} product={product} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-start items-center mt-8 space-x-2">
                    <button
                      onClick={handlePrev}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 border rounded ${
                        currentPage === 1
                          ? "bg-gray-200 flex items-center text-gray-400 cursor-not-allowed"
                          : "bg-white flex items-center hover:bg-[#1DA14D] hover:text-white"
                      }`}
                    >
                      <MdKeyboardDoubleArrowLeft />
                      PREV
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => handlePageClick(i + 1)}
                        className={`px-3 py-1 border rounded ${
                          currentPage === i + 1
                            ? "bg-black text-white border-black"
                            : "bg-white hover:bg-[#1DA14D] hover:text-white"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}

                    <button
                      onClick={handleNext}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 border rounded ${
                        currentPage === totalPages
                          ? "bg-gray-200 flex items-center text-gray-400 cursor-not-allowed"
                          : "bg-white flex items-center hover:bg-[#1DA14D] hover:text-white"
                      }`}
                    >
                      NEXT
                      <MdKeyboardDoubleArrowRight />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductListView;
